// ── ActivityTicker — Last 20 fills display ──────────────────────
// Reads from accountStore.activityTicker. Shows a compact list of
// recent fills with ticker, side, count, price, and relative time.

import { useAccountStore } from '../stores/accountStore';

function timeAgo(isoString: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

function truncateTicker(ticker: string): string {
  // Show last meaningful segment: e.g., "KXNBAGAME-26APR04DETPHI-DET" → "...DETPHI-DET"
  if (ticker.length <= 20) return ticker;
  const parts = ticker.split('-');
  if (parts.length >= 2) {
    const tail = parts.slice(-2).join('-');
    return `...${tail}`;
  }
  return `...${ticker.slice(-16)}`;
}

export function ActivityTicker() {
  const fills = useAccountStore((s) => s.activityTicker);
  const status = useAccountStore((s) => s.status);

  if (status !== 'connected') return null;
  if (fills.length === 0) {
    return (
      <div className="px-3 py-2 text-xs text-gray-500">
        No recent fills
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {fills.map((fill) => (
        <div
          key={fill.fillId}
          className="flex items-center justify-between gap-2 rounded px-2 py-1 text-xs hover:bg-gray-800/50"
        >
          <div className="flex items-center gap-2">
            <span className={fill.action === 'buy' ? 'font-medium text-green-400' : 'font-medium text-red-400'}>
              {fill.action.toUpperCase()}
            </span>
            <span className="text-gray-400">{fill.count}</span>
            <span className={fill.side === 'yes' ? 'text-blue-400' : 'text-orange-400'}>
              {fill.side.toUpperCase()}
            </span>
            <span className="font-mono text-gray-500">{truncateTicker(fill.ticker)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-300">@ {fill.price}¢</span>
            <span className="text-gray-600">{timeAgo(fill.createdAt)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
